import { compact, useIntl, useSettings } from '@capsicum/common';
import { BaseToggleInput, Section, SectionsPage, Skeleton, SkeletonItem, useWebPush } from '@capsicum/common-web';
import { useMarketingConsent } from '@capsicum/features/onboard/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

const BASE_TEST_ID = 'settings-notifications-page';

export const NotificationsPage = () => {
	const { $t } = useIntl();
	const {
		canRequestPermission,
		displayEnablePushNotificationsBySettingsModal,
		isPermissionGranted,
		requestNotificationsPermission
	} = useWebPush();

	const { settings } = useSettings();

	const { fetchMarketingEmailConsentLoading, isMarketingEmailConsentChecked, switchMarketingConsentAcceptance } =
		useMarketingConsent();

	const EmailToggle = useMemo(
		() =>
			fetchMarketingEmailConsentLoading ? (
				<Skeleton>
					<SkeletonItem borderRadius={32} height={25} width={48} />
				</Skeleton>
			) : (
				<BaseToggleInput
					name="devtools-translation-keys"
					testID={`${BASE_TEST_ID}-email-toggle`}
					value={isMarketingEmailConsentChecked}
					onChange={switchMarketingConsentAcceptance}
				/>
			),

		[isMarketingEmailConsentChecked, switchMarketingConsentAcceptance, fetchMarketingEmailConsentLoading]
	);

	const [areNotificationsAllowed, setNotifications] = useState(isPermissionGranted);

	useEffect(() => {
		setNotifications(isPermissionGranted);
	}, [isPermissionGranted]);

	const handleTogglePushNotifications = useCallback(() => {
		if (!canRequestPermission || isPermissionGranted) {
			displayEnablePushNotificationsBySettingsModal();

			return;
		}

		requestNotificationsPermission();
	}, [displayEnablePushNotificationsBySettingsModal, canRequestPermission, isPermissionGranted]);

	const notificationItems = useMemo(
		() =>
			compact([
				settings.isMarketingEmailConsentVisible
					? {
							element: EmailToggle,
							isTrailingIconReversible: true,
							testID: `${BASE_TEST_ID}-email`,
							title: $t('onboard.notifications.emailNotifications')
						}
					: undefined,
				{
					element: (
						<BaseToggleInput
							name="devtools-translation-keys"
							testID={`${BASE_TEST_ID}-push-toggle`}
							value={areNotificationsAllowed}
							onChange={handleTogglePushNotifications}
						/>
					),
					isTrailingIconReversible: true,
					testID: `${BASE_TEST_ID}-push`,
					title: $t('onboard.notifications.webNotifications')
				}
			]),
		[EmailToggle, areNotificationsAllowed, handleTogglePushNotifications, settings.isMarketingEmailConsentVisible]
	);

	return <SectionsPage sections={[<Section items={notificationItems} key="0" />]} />;
};
